import { ReactElement, useCallback, useState, useEffect, useMemo } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GoBackButton, Button, ConfirmModal, Table } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IMethod } from '~/models/Method';
import { MethodService } from '~/services';

import * as S from './styles';

interface IMethodDetailsParams {
  methodId: string;
}

function MethodDetails(): ReactElement {
  const { setLoading } = useLoading();
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmDuplicateModal, setConfirmDuplicateModal] = useState(false);
  const [method, setMethod] = useState<IMethod>();
  const router = useHistory();
  const { methodId } = useParams<IMethodDetailsParams>();

  if (!methodId) {
    toast.error('Método inválido.');
    router.goBack();
  }

  const handleOpenConfirmDeleteModal = useCallback(() => {
    setConfirmDeleteModal(true);
  }, []);

  const handleDeleteMethod = useCallback(async () => {
    try {
      setLoading(true);
      const result = await MethodService.deleteMethod(methodId);
      if (result.status === 200) {
        toast.success('Método deletado com sucesso!');
        router.goBack();
      } else {
        toast.error('Ocorreu um problema ao deletar o método!');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao deletar o método!');
    }
  }, []);

  const handleOpenConfirmDuplicateModal = useCallback(() => {
    setConfirmDuplicateModal(true);
  }, []);

  const handleDuplicateMethod = useCallback(async () => {
    try {
      setLoading(true);

      if (!method) return;
      const result = await MethodService.createMethod(method);
      if (result.status.toString().includes('2')) {
        toast.success('Método duplicado com sucesso!');
        router.goBack();
      } else {
        toast.error('Ocorreu um problema ao duplicar o método!');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao duplicar o método!');
    }
  }, [method, router]);

  const handleGetMethod = useCallback(async () => {
    setLoading(true);

    const response = await MethodService.getOneMethod(methodId!);
    setMethod(response.data);

    setLoading(false);
  }, [methodId]);

  const handleNavigateToEditMethod = useCallback(() => {
    router.push(`/ilab/methods/edit/${methodId}`);
  }, [methodId, router]);

  useEffect(() => {
    handleGetMethod();
  }, [handleGetMethod]);

  const MethodSeriesMemo = useMemo(
    () => (
      <Table
        noItems={Object.values(method?.series_names ?? {}).length === 0}
        colsNames={[
          'Séries',
          'Número de repetições',
          'Tempo de ação',
          'Intervalo de recuperação (entre séries)',
        ]}
      >
        {Object.values(method?.series_names ?? {}).map(
          (serie: any, index: number) => (
            <tr>
              <td>{serie}</td>
              <td>
                {
                  method?.series_number_of_repetitions[
                    `repetitions_serie_${index + 1}`
                  ]
                }
              </td>
              <td>
                {method?.series_actions_time[
                  `action_time_serie_${index + 1}`
                ]?.concat(' minuto(s)')}
              </td>
              <td>
                {method?.series_recovery_Time[
                  `time_of_recovery_serie_${index + 1}`
                ]?.concat(' minuto(s)')}
              </td>
            </tr>
          ),
        )}
      </Table>
    ),
    [method],
  );

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.Header>
          <S.Title>Detalhes do Método</S.Title>
        </S.Header>
        <S.InfosContainer>
          <S.InfosItem>
            <S.InfosItemTitle>Método</S.InfosItemTitle>
            <S.InfosItemValue>{method?.tranning_method}</S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Princípio ativo</S.InfosItemTitle>
            <S.InfosItemValue>{method?.method_principle}</S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Execução</S.InfosItemTitle>
            <S.InfosItemValue>{method?.execution}</S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Volume N° de séries</S.InfosItemTitle>
            <S.InfosItemValue>{method?.series_number}</S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Volume N° de repetições</S.InfosItemTitle>
            <S.InfosItemValue>{method?.volume_total}</S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Tempo de ação total</S.InfosItemTitle>
            <S.InfosItemValue>
              {method?.total_action?.concat(' minuto(s)')}
            </S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Intervalo de recuperação total:</S.InfosItemTitle>
            <S.InfosItemValue>
              {method?.interval_recovery_total?.concat(' minuto(s)')}
            </S.InfosItemValue>
          </S.InfosItem>
          <S.InfosItem>
            <S.InfosItemTitle>Tempo total</S.InfosItemTitle>
            <S.InfosItemValue>
              {method?.total_time?.concat(' minuto(s)')}
            </S.InfosItemValue>
          </S.InfosItem>
          {Object.values(method?.series_names ?? {}).length !== 0
            ? MethodSeriesMemo
            : 'Nenhuma série cadastrada'}
          <S.InfosItemExpanded>
            <S.InfosItemTitle>Descrição técnica</S.InfosItemTitle>
            <S.InfosItemValue>{method?.technical_description}</S.InfosItemValue>
          </S.InfosItemExpanded>
        </S.InfosContainer>
        <S.Footer>
          <Button
            variant="black"
            textContent="Editar"
            onClick={handleNavigateToEditMethod}
            size="small"
            extraStyles={{
              maxWidth: '20rem',
              maxHeight: '3.5rem',
            }}
          />
          <Button
            variant="yellow"
            textContent="Duplicar"
            onClick={handleOpenConfirmDuplicateModal}
            size="small"
            extraStyles={{
              maxWidth: '20rem',
              maxHeight: '3.5rem',
            }}
          />
          <Button
            variant="red"
            textContent="Excluir"
            onClick={handleOpenConfirmDeleteModal}
            size="small"
            extraStyles={{
              maxWidth: '20rem',
              maxHeight: '3.5rem',
            }}
          />
        </S.Footer>
      </S.Container>

      <ConfirmModal
        modalIsVisible={confirmDeleteModal}
        setModalIsVisible={setConfirmDeleteModal}
        handleSubmit={handleDeleteMethod}
        textContent="Tem certeza de que deseja excluir esse método?"
        title="Deletar método"
      />

      <ConfirmModal
        modalIsVisible={confirmDuplicateModal}
        setModalIsVisible={setConfirmDuplicateModal}
        handleSubmit={handleDuplicateMethod}
        textContent="Tem certeza de que deseja duplicar esse método?"
        title="Duplicar método"
      />
    </>
  );
}

export default MethodDetails;
